import {
  extractFirebaseStoragePath,
  hasFirebaseMediaToken,
  isFirebaseStorageUrl,
} from "@shared/lib/services/firebase.service";
import { getCachedEditorMediaDataUrl } from "./editorMedia";

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Failed to read media"));
    };
    reader.onerror = () => reject(new Error("Failed to read media"));
    reader.readAsDataURL(blob);
  });
}

function samePath(a: string, b: string): boolean {
  try {
    const ua = new URL(a);
    const ub = new URL(b);
    return ua.origin === ub.origin && ua.pathname === ub.pathname;
  } catch {
    return a === b;
  }
}

/** Prefer live editor <img src> when it already has &token=. */
export function preferTokenizedDomUrl(url: string): string {
  if (!url || hasFirebaseMediaToken(url)) return url;

  const path = isFirebaseStorageUrl(url)
    ? extractFirebaseStoragePath(url)
    : null;

  for (const img of document.querySelectorAll<HTMLImageElement>(
    "img.bn-visual-media, .bn-editor img"
  )) {
    const src = img.currentSrc || img.src;
    if (!src || !hasFirebaseMediaToken(src)) continue;
    if (samePath(src, url)) return src;
    if (path && extractFirebaseStoragePath(src) === path) return src;
  }

  return url;
}

async function fetchAsDataUrl(url: string): Promise<string> {
  const response = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!response.ok) throw new Error(`Failed to fetch media: ${response.status}`);
  return blobToDataUrl(await response.blob());
}

function tryCanvasFromDomImage(url: string): string | null {
  const match = [
    ...document.querySelectorAll<HTMLImageElement>(
      "img.bn-visual-media, .bn-editor img, img"
    ),
  ].find((img) => samePath(img.currentSrc || img.src, url));

  if (!match?.complete || !match.naturalWidth) return null;

  try {
    const canvas = document.createElement("canvas");
    canvas.width = match.naturalWidth;
    canvas.height = match.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(match, 0, 0);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

export function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to decode image"));
    img.src = dataUrl;
  });
}

/** Resolve image bytes for PDF/DOC: cache → tokenized DOM URL → fetch → canvas. */
export async function resolveImageDataUrl(url: string): Promise<string> {
  if (!url) throw new Error("Missing image url");
  if (url.startsWith("data:image/")) return url;

  const resolvedUrl = preferTokenizedDomUrl(url);
  const cached =
    getCachedEditorMediaDataUrl(resolvedUrl) ||
    getCachedEditorMediaDataUrl(url);
  if (cached) return cached;

  if (resolvedUrl.startsWith("blob:") || resolvedUrl.startsWith("data:")) {
    return fetchAsDataUrl(resolvedUrl);
  }

  if (isFirebaseStorageUrl(resolvedUrl) && !hasFirebaseMediaToken(resolvedUrl)) {
    throw new Error("Firebase media URL is missing download token");
  }

  try {
    return await fetchAsDataUrl(resolvedUrl);
  } catch {
    const fromDom =
      tryCanvasFromDomImage(resolvedUrl) || tryCanvasFromDomImage(url);
    if (fromDom) return fromDom;
    throw new Error("Failed to resolve image for export");
  }
}

export function dataUrlFormat(dataUrl: string): "PNG" | "JPEG" | "WEBP" {
  if (dataUrl.startsWith("data:image/jpeg") || dataUrl.startsWith("data:image/jpg")) {
    return "JPEG";
  }
  if (dataUrl.startsWith("data:image/webp")) return "WEBP";
  return "PNG";
}
